import axios from '~/utils/axios';
import { urlWithParams } from '~/utils';
import { ApprovalCase, ApprovalCaseActionType, ApprovalCaseDetail } from '~/types/approvalCase';
import { Moment } from '~/types';

export type ApprovalCaseClaimed = 'ANY' | 'ME' | 'UNCLAIMED' | 'OTHER';

export type ApprovalCaseFilters = {
  objectType?: string;
  objectId?: string;
  actionType?: ApprovalCaseActionType;
  claimed?: ApprovalCaseClaimed;
  requestedById?: string;
  withAllQueue?: boolean;
  status?: string;
  submittedFrom?: Moment | null;
  submittedTo?: Moment | null;
  completedFrom?: Moment | null;
  completedTo?: Moment | null;
};

type ListResponse = {
  cursors: {
    next?: string;
  };
  items?: ApprovalCase[];
};

interface GetApprovalsProps {
  next?: string | null;
  filters?: ApprovalCaseFilters;
}

export const getPendingApprovals = (props: GetApprovalsProps): Promise<{ data: ListResponse }> => {
  const { filters, next } = props;
  const { submittedFrom, submittedTo, ...rest } = filters || {};
  return axios.get<ListResponse>(
    urlWithParams('/approvals/queue', {
      next,
      limit: 50,
      submittedFrom: submittedFrom?.toISOString(),
      submittedTo: submittedTo?.toISOString(),
      ...rest,
    })
  );
};

export const getCompletedApprovals = (
  props: GetApprovalsProps
): Promise<{ data: ListResponse }> => {
  const { filters, next } = props;
  const { submittedFrom, submittedTo, completedFrom, completedTo, ...rest } = filters || {};
  return axios.get<ListResponse>(
    urlWithParams('/approvals/history', {
      next,
      limit: 50,
      submittedFrom: submittedFrom?.toISOString(),
      submittedTo: submittedTo?.toISOString(),
      completedFrom: completedFrom?.toISOString(),
      completedTo: completedTo?.toISOString(),
      ...rest,
    })
  );
};

export type ApprovalAction = 'CLAIM' | 'UNCLAIM' | 'APPROVE' | 'REJECT' | 'CANCEL';

type ActionsResponse = {
  actions: ApprovalAction[];
};

export const getApprovalCaseActions = (id: string): Promise<{ data: ActionsResponse }> => {
  return axios.get<ActionsResponse>(`/approvals/queue/${id}/actions`);
};

export const claimPendingApproval = (params: { id: string }) => {
  return axios.post(`/approvals/queue/${params.id}/claim`, { force: true });
};

export const unclaimPendingApproval = (params: { id: string }) => {
  return axios.post(`/approvals/queue/${params.id}/unclaim`);
};

export const approvePendingApproval = (params: { id: string; note: string }) => {
  return axios.post(`/approvals/queue/${params.id}/approve`, { note: params.note });
};

export const rejectPendingApproval = (params: { id: string; note: string }) => {
  return axios.post(`/approvals/queue/${params.id}/reject`, { note: params.note });
};

export const cancelPendingApproval = (params: { id: string; note: string }) => {
  return axios.post(`/approvals/queue/${params.id}/cancel`, { note: params.note });
};

export const getApprovalCaseFromQueue = (id: string): Promise<ApprovalCaseDetail> => {
  return axios.get<any>(`/approvals/queue/${id}`).then((res) => res.data);
};

export const getApprovalCaseUnified = (id: string): Promise<ApprovalCaseDetail> => {
  return axios.get<any>(`/approvals/${id}`).then((res) => res.data);
};

export const getApprovalCaseFromHistory = (id: string): Promise<ApprovalCaseDetail> => {
  return axios.get<any>(`/approvals/history/${id}`).then((res) => res.data);
};

// could be used without permissions
export const getApprovalCaseForObject = (objectType: string, objectId: string) => {
  return axios.get<Partial<ApprovalCaseDetail>[]>(`/approvals/queue/forObject`, {
    params: { objectType, objectId },
  });
};
